/**
 * Base Handler
 * Abstract base class for all WebSocket message handlers
 */

import { LoggerService } from "../../logger.service";
import type { WebSocketResponse } from "@/types/generated-types";

export abstract class BaseHandler {
  protected logger: LoggerService;

  constructor() {
    this.logger = new LoggerService(this.constructor.name);
  }

  /**
   * Handle incoming WebSocket message
   * @param message - The WebSocket message
   * @param context - Handler context (auth state, player info, etc.)
   * @returns Promise that resolves when handling is complete
   */
  abstract handle(
    message: WebSocketResponse,
  ): Promise<void>;

  /**
   * Check if handler can process this message type
   * @param messageType - The message type to check
   * @returns True if this handler can process the message type
   */
  abstract canHandle(messageType: string): boolean;

  /**
   * Validate message before processing
   * @param message - The message to validate
   * @returns True if message is valid
   */
  protected validateMessage(message: WebSocketResponse): boolean {
    return message.result === "ok" || message.result === "error";
  }

  /**
   * Log handler activity
   * @param message - Log message
   * @param data - Additional data to log
   */
  protected log(message: string, data?: unknown): void {
    console.log(`[unibo-widget:websocket:handler] ${message}`, data || "");
  }

  /**
   * Log errors
   * @param message - Error message
   * @param error - Error object
   */
  protected logError(message: string, error: unknown): void {
    console.error(`[unibo-widget:websocket:handler] ${message}`, error);
  }
}
